import React, { useState, useEffect, useRef, type ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme, type ThemeColors } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import TextInput from '../../components/ui/TextInput';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Label';
import { Switch } from '../../components/ui/Switch';
import { Select } from '../../components/ui/Select';
import NumberInput from '../../components/ui/NumberInput';
import { Textarea } from '../../components/ui/Textarea';
import {
  useApplicationSettings,
  ApplicationSettings,
  useCompanyProfile,
  updateCompanyProfile,
  uploadCompanyLogo,
  useCompanyLogoBlobUrl,
} from './useData';
import { useSettings } from '../../contexts/SettingsContext';
import { APP_CONFIG } from '../../config/appConfig';
import api from '../../services/api';
import { cn } from '../../lib/utils';
import Card from '../../components/ui/Card';
import {
  Bot,
  Building2,
  Cog,
  LayoutDashboard,
  Mail,
  Save,
  Settings2,
  Sparkles,
  Table2,
} from 'lucide-react';
import type { EmbeddedInConfigurationHub } from '../configuration-settings/models';

export type ApplicationSettingsFormProps = EmbeddedInConfigurationHub;

const defaultDashboardGrid = APP_CONFIG.dashboard;

const SETTINGS_SECTION_TABS = [
  { id: 'features', label: 'App features', description: 'Search, export, chrome', icon: Sparkles },
  { id: 'email', label: 'Email', description: 'Compose, send & drafts', icon: Mail },
  { id: 'ai', label: 'AI', description: 'Assistant & email AI', icon: Bot },
  { id: 'general', label: 'General', description: 'Name & API timeout', icon: Cog },
  { id: 'company', label: 'Company', description: 'Logo & letterhead', icon: Building2 },
  { id: 'tables', label: 'Tables', description: 'Lists & presets', icon: Table2 },
  { id: 'dashboard', label: 'Dashboard', description: 'Grid & widgets', icon: LayoutDashboard },
] as const;

type ApplicationSettingsTab = (typeof SETTINGS_SECTION_TABS)[number]['id'];

function SettingsGroup({
  title,
  description,
  children,
  colors,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  colors: ThemeColors;
}) {
  return (
    <section className={cn('overflow-hidden rounded-2xl border', colors.border, colors.surface)}>
      <div className={cn('border-b px-4 py-3 sm:px-5', colors.border, 'bg-gray-50/70 dark:bg-gray-900/40')}>
        <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
        {description ? (
          <p className={cn('mt-0.5 text-xs leading-relaxed', colors.textMuted)}>{description}</p>
        ) : null}
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">{children}</div>
    </section>
  );
}

function SettingRow({
  id,
  title,
  description,
  effect,
  control,
  colors,
}: {
  id: string;
  title: string;
  description: string;
  effect?: string;
  control: ReactNode;
  colors: ThemeColors;
}) {
  return (
    <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 sm:px-5">
      <div className="min-w-0 flex-1">
        <Label htmlFor={id} className="mb-1">
          {title}
        </Label>
        <p className={cn('text-xs leading-relaxed', colors.textMuted)}>{description}</p>
        {effect ? (
          <p className={cn('mt-1.5 text-[11px] leading-relaxed', colors.textMuted)}>
            <span className={cn('font-medium', colors.textSecondary)}>Effect: </span>
            {effect}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center sm:pt-0.5">{control}</div>
    </div>
  );
}

interface ApplicationSettingsNavProps {
  activeTab: ApplicationSettingsTab;
  onChange: (tab: ApplicationSettingsTab) => void;
}

const ApplicationSettingsNav: React.FC<ApplicationSettingsNavProps> = ({ activeTab, onChange }) => {
  const { colors } = useTheme();

  const renderButton = (item: (typeof SETTINGS_SECTION_TABS)[number], compact: boolean) => {
    const Icon = item.icon;
    const active = activeTab === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => onChange(item.id)}
        className={cn(
          'text-left transition-all duration-200',
          compact
            ? cn(
                'inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium',
                active
                  ? cn(colors.primary, colors.primaryText, 'border-transparent shadow-sm')
                  : cn(colors.border, colors.textSecondary, colors.surface, colors.interactiveHover),
              )
            : cn(
                'flex w-full items-center gap-3 rounded-xl px-3 py-3',
                active
                  ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                  : cn(colors.textSecondary, colors.interactiveHover),
              ),
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-4 w-4' : 'h-9 w-9',
            !compact && (active ? 'bg-white/15' : cn(colors.surface, 'border', colors.border)),
          )}
        >
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        {compact ? (
          <span>{item.label}</span>
        ) : (
          <span className="min-w-0 flex-1">
            <span className={cn('block text-sm font-medium', active ? colors.primaryText : colors.text)}>
              {item.label}
            </span>
            <span className={cn('mt-0.5 block truncate text-xs', active ? 'opacity-90' : colors.textMuted)}>
              {item.description}
            </span>
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div className={cn('flex gap-2 overflow-x-auto border-b p-3 lg:hidden', colors.border)}>
        {SETTINGS_SECTION_TABS.map((item) => renderButton(item, true))}
      </div>
      <nav className={cn('hidden lg:flex lg:w-60 lg:shrink-0 lg:flex-col lg:gap-1 lg:border-r lg:p-3', colors.border)}>
        {SETTINGS_SECTION_TABS.map((item) => renderButton(item, false))}
      </nav>
    </>
  );
};

const ApplicationSettingsForm: React.FC<ApplicationSettingsFormProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const isAdmin = user?.role === 'ADMIN';

  const { applicationSettings, isLoading: loadingApp, mutate: mutateApp } = useApplicationSettings();
  const { refreshSettings } = useSettings();

  const [appFormData, setAppFormData] = useState<Partial<ApplicationSettings>>({
    dashboardGridColumns: defaultDashboardGrid.gridColumns,
    dashboardStatCardWidth: defaultDashboardGrid.statCardWidth,
    dashboardListWidgetWidth: defaultDashboardGrid.listWidgetWidth,
    dashboardChartWidth: defaultDashboardGrid.chartWidth,
  });
  const [saving, setSaving] = useState(false);
  const [settingsTab, setSettingsTab] = useState<ApplicationSettingsTab>('features');

  const { companyProfile, mutate: mutateCompanyProfile } = useCompanyProfile();
  const { logoBlobUrl: companyLogoBlobUrl, loading: companyLogoLoading } = useCompanyLogoBlobUrl(
    companyProfile?.logoPath,
  );
  const [companyFormData, setCompanyFormData] = useState({
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    signatoryName: '',
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const lastSyncedCompanyProfileIdRef = useRef<number | null>(null);

  const handleSettingsTabChange = (value: ApplicationSettingsTab) => {
    setSettingsTab(value);
    if (value === 'company' && companyProfile) {
      lastSyncedCompanyProfileIdRef.current = companyProfile.id;
      setCompanyFormData({
        companyName: companyProfile.companyName ?? '',
        companyAddress: companyProfile.companyAddress ?? '',
        companyPhone: companyProfile.companyPhone ?? '',
        companyEmail: companyProfile.companyEmail ?? '',
        signatoryName: companyProfile.signatoryName ?? '',
      });
    }
  };

  useEffect(() => {
    if (!companyProfile) return;
    if (settingsTab === 'company') return;
    lastSyncedCompanyProfileIdRef.current = companyProfile.id;
    setCompanyFormData({
      companyName: companyProfile.companyName ?? '',
      companyAddress: companyProfile.companyAddress ?? '',
      companyPhone: companyProfile.companyPhone ?? '',
      companyEmail: companyProfile.companyEmail ?? '',
      signatoryName: companyProfile.signatoryName ?? '',
    });
  }, [companyProfile, settingsTab]);

  useEffect(() => {
    if (applicationSettings) {
      const num = (v: unknown): number | undefined => {
        if (v === null || v === undefined) return undefined;
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : undefined;
      };
      setAppFormData({
        enableAdvancedSearch: applicationSettings.enableAdvancedSearch,
        enableExport: applicationSettings.enableExport,
        enableTablePresets: applicationSettings.enableTablePresets,
        enableFooter: applicationSettings.enableFooter,
        enableAiEmails: applicationSettings.enableAiEmails ?? false,
        enableAiAssistant: applicationSettings.enableAiAssistant ?? false,
        compactEmailView: applicationSettings.compactEmailView ?? false,
        emailUndoSendDelaySeconds: applicationSettings.emailUndoSendDelaySeconds ?? 10,
        emailDraftAutosaveSeconds: applicationSettings.emailDraftAutosaveSeconds ?? 30,
        emailKeepDraftAfterSend: applicationSettings.emailKeepDraftAfterSend ?? false,
        emailShowAccountDrafts: applicationSettings.emailShowAccountDrafts ?? false,
        emailSendTimeout: applicationSettings.emailSendTimeout ?? 60000,
        emailSendWithAttachmentsTimeout:
          applicationSettings.emailSendWithAttachmentsTimeout ?? 300000,
        emailAttachmentTransferMode:
          applicationSettings.emailAttachmentTransferMode === 'multipart'
            ? 'multipart'
            : 'json_base64',
        emailAttachmentMaxSizeMb: applicationSettings.emailAttachmentMaxSizeMb ?? 25,
        apiTimeout: applicationSettings.apiTimeout,
        appName: applicationSettings.appName || '',
        defaultTablePageSize: applicationSettings.defaultTablePageSize,
        defaultTableDensity: applicationSettings.defaultTableDensity,
        pageSizeOptions: applicationSettings.pageSizeOptions || [50, 100, 200, 500],
        dashboardGridColumns: num(applicationSettings.dashboardGridColumns) ?? defaultDashboardGrid.gridColumns,
        dashboardStatCardWidth: num(applicationSettings.dashboardStatCardWidth) ?? defaultDashboardGrid.statCardWidth,
        dashboardListWidgetWidth:
          num(applicationSettings.dashboardListWidgetWidth) ?? defaultDashboardGrid.listWidgetWidth,
        dashboardChartWidth: num(applicationSettings.dashboardChartWidth) ?? defaultDashboardGrid.chartWidth,
      });
    }
  }, [applicationSettings]);

  const saveApplicationSettings = async () => {
    setSaving(true);

    try {
      const payload: Record<string, unknown> = { ...appFormData };
      await api.patch('/settings/application', payload);
      await mutateApp();
      refreshSettings();
      showSuccess('Application settings updated successfully');
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(message || 'Failed to update application settings');
    } finally {
      setSaving(false);
    }
  };

  const handleCompanyProfileSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault?.();
    setSavingCompany(true);
    try {
      const updated = await updateCompanyProfile(companyFormData);
      lastSyncedCompanyProfileIdRef.current = updated.id;
      setCompanyFormData({
        companyName: updated.companyName ?? '',
        companyAddress: updated.companyAddress ?? '',
        companyPhone: updated.companyPhone ?? '',
        companyEmail: updated.companyEmail ?? '',
        signatoryName: updated.signatoryName ?? '',
      });
      await mutateCompanyProfile(updated, false);
      await mutateCompanyProfile();
      showSuccess('Company details updated');
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(msg || 'Failed to update company details');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      await uploadCompanyLogo(file);
      await mutateCompanyProfile();
      showSuccess('Logo uploaded');
      e.target.value = '';
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(msg || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  if (!isAdmin) {
    if (embedded) {
      return (
        <Card variant="outlined" className="p-6 text-center">
          <p className={cn('text-sm', colors.text)}>
            Application settings are only available to administrators.
          </p>
        </Card>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="text-center">
          <h2 className={cn('mb-2 text-2xl font-bold', colors.text)}>Access denied</h2>
          <p className={cn('text-sm', colors.textMuted)}>
            Application settings are only available to administrators.
          </p>
        </div>
      </div>
    );
  }

  if (loadingApp) {
    if (embedded) {
      return (
        <div className="py-10">
          <LoadingSpinner message="Loading settings…" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner message="Loading settings…" size="large" />
      </div>
    );
  }

  const applicationSaveFooter = (
    <div className={cn('flex flex-col gap-2 border-t pt-6 sm:flex-row sm:items-center sm:justify-between', colors.border)}>
      <p className={cn('text-xs', colors.textMuted)}>
        Changes apply to all users after you save.
      </p>
      <Button
        type="button"
        variant="default"
        loading={saving}
        disabled={saving}
        onClick={() => void saveApplicationSettings()}
        className="gap-2 self-end"
      >
        <Save className="h-4 w-4" aria-hidden />
        Save application settings
      </Button>
    </div>
  );

  const currentTab = SETTINGS_SECTION_TABS.find((t) => t.id === settingsTab);

  const settingsPanels = (
    <>
      {settingsTab === 'features' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Turn on or off capabilities available across the whole application. Email and AI options live
            in their own sections.
          </p>

          <SettingsGroup
            title="Core capabilities"
            description="Tools users see in lists, search, and the page chrome."
            colors={colors}
          >
            <SettingRow
              id="enableAdvancedSearch"
              title="Advanced search"
              description="Show advanced filters and query options in modules that support them."
              effect="Off hides advanced search UI; basic search still works where available."
              colors={colors}
              control={
                <Switch
                  id="enableAdvancedSearch"
                  checked={appFormData.enableAdvancedSearch ?? true}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, enableAdvancedSearch: e.target.checked })
                  }
                />
              }
            />
            <SettingRow
              id="enableExport"
              title="Data export"
              description="Allow exporting list data (for example to Excel) from supported modules."
              effect="Off removes export actions from toolbars and menus."
              colors={colors}
              control={
                <Switch
                  id="enableExport"
                  checked={appFormData.enableExport ?? true}
                  onChange={(e) => setAppFormData({ ...appFormData, enableExport: e.target.checked })}
                />
              }
            />
            <SettingRow
              id="enableFooter"
              title="Application footer"
              description="Show the footer bar at the bottom of the app (sync time, copyright, etc.)."
              effect="Off hides the footer for every user."
              colors={colors}
              control={
                <Switch
                  id="enableFooter"
                  checked={appFormData.enableFooter ?? true}
                  onChange={(e) => setAppFormData({ ...appFormData, enableFooter: e.target.checked })}
                />
              }
            />
          </SettingsGroup>

          {applicationSaveFooter}
        </div>
      )}

      {settingsTab === 'email' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Controls how email looks when reading, how send works, and how drafts are saved and shared.
          </p>

          <SettingsGroup
            title="Reading"
            description="Layout when viewing a single message."
            colors={colors}
          >
            <SettingRow
              id="compactEmailView"
              title="Compact message view"
              description="Use a denser header and layout when opening one email."
              effect="On = shorter header and fewer lines; Off = standard detail layout."
              colors={colors}
              control={
                <Switch
                  id="compactEmailView"
                  checked={appFormData.compactEmailView ?? false}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, compactEmailView: e.target.checked })
                  }
                />
              }
            />
          </SettingsGroup>

          <SettingsGroup
            title="Sending"
            description="Delay before outbound mail leaves the outbox, and how long the browser waits for send to finish."
            colors={colors}
          >
            <SettingRow
              id="emailUndoSendDelaySeconds"
              title="Undo-send window (seconds)"
              description="How long a sent message stays in Outbox so users can cancel it."
              effect="0 = send immediately. Typical values: 5–30 seconds."
              colors={colors}
              control={
                <div className="w-28">
                  <NumberInput
                    id="emailUndoSendDelaySeconds"
                    name="emailUndoSendDelaySeconds"
                    value={appFormData.emailUndoSendDelaySeconds ?? 10}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        emailUndoSendDelaySeconds:
                          e.target.value == null
                            ? 10
                            : Math.max(0, Math.min(300, Number(e.target.value))),
                      })
                    }
                    min={0}
                    max={300}
                    step={1}
                    allowDecimals={false}
                    showClearButton={false}
                    disabled={!isAdmin}
                  />
                </div>
              }
            />
            <SettingRow
              id="emailSendTimeout"
              title="Send timeout — no attachments (ms)"
              description="How long Compose waits for send/reply/forward when there are no attachments."
              effect="Default 60000 (1 minute). Range 10000–600000."
              colors={colors}
              control={
                <div className="w-32">
                  <NumberInput
                    id="emailSendTimeout"
                    name="emailSendTimeout"
                    value={appFormData.emailSendTimeout ?? 60000}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        emailSendTimeout:
                          e.target.value == null
                            ? 60000
                            : Math.max(10000, Math.min(600000, Number(e.target.value))),
                      })
                    }
                    min={10000}
                    max={600000}
                    step={1000}
                    allowDecimals={false}
                    showClearButton={false}
                    disabled={!isAdmin}
                  />
                </div>
              }
            />
            <SettingRow
              id="emailSendWithAttachmentsTimeout"
              title="Send timeout — with attachments (ms)"
              description="How long Compose waits when sending with attachments (upload + mail server)."
              effect="Default 300000 (5 minutes). Use 300000–600000 for ~20 MB files on slower links."
              colors={colors}
              control={
                <div className="w-32">
                  <NumberInput
                    id="emailSendWithAttachmentsTimeout"
                    name="emailSendWithAttachmentsTimeout"
                    value={appFormData.emailSendWithAttachmentsTimeout ?? 300000}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        emailSendWithAttachmentsTimeout:
                          e.target.value == null
                            ? 300000
                            : Math.max(30000, Math.min(600000, Number(e.target.value))),
                      })
                    }
                    min={30000}
                    max={600000}
                    step={1000}
                    allowDecimals={false}
                    showClearButton={false}
                    disabled={!isAdmin}
                  />
                </div>
              }
            />
            <SettingRow
              id="emailAttachmentTransferMode"
              title="Attachment transfer mode"
              description="How Compose sends file attachments to the server."
              effect="JSON Base64 is the classic path (single request). Multipart uploads files first, then sends a small JSON body with secure paths — better for large files."
              colors={colors}
              control={
                <div className="w-48">
                  <Select
                    value={appFormData.emailAttachmentTransferMode ?? 'json_base64'}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        emailAttachmentTransferMode:
                          e.target.value === 'multipart' ? 'multipart' : 'json_base64',
                      })
                    }
                    disabled={!isAdmin}
                    options={[
                      { value: 'json_base64', label: 'JSON Base64' },
                      { value: 'multipart', label: 'Multipart upload' },
                    ]}
                  />
                </div>
              }
            />
            <SettingRow
              id="emailAttachmentMaxSizeMb"
              title="Attachment size limit (MB)"
              description="Maximum total size of all attachments on a Compose message."
              effect="Default 25. Range 1–100. Applies to new compose, reply, and forward."
              colors={colors}
              control={
                <div className="w-28">
                  <NumberInput
                    id="emailAttachmentMaxSizeMb"
                    name="emailAttachmentMaxSizeMb"
                    value={appFormData.emailAttachmentMaxSizeMb ?? 25}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        emailAttachmentMaxSizeMb:
                          e.target.value == null
                            ? 25
                            : Math.max(1, Math.min(100, Number(e.target.value))),
                      })
                    }
                    min={1}
                    max={100}
                    step={1}
                    allowDecimals={false}
                    showClearButton={false}
                    disabled={!isAdmin}
                  />
                </div>
              }
            />
          </SettingsGroup>

          <SettingsGroup
            title="Compose drafts"
            description="Automatic saving and what happens to drafts after send."
            colors={colors}
          >
            <SettingRow
              id="emailDraftAutosaveSeconds"
              title="Auto-save interval (seconds)"
              description="How often Compose saves the current message to Drafts while editing."
              effect="0 = no timer (manual save and window blur still save). Default is 30."
              colors={colors}
              control={
                <div className="w-28">
                  <NumberInput
                    id="emailDraftAutosaveSeconds"
                    name="emailDraftAutosaveSeconds"
                    value={appFormData.emailDraftAutosaveSeconds ?? 30}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        emailDraftAutosaveSeconds:
                          e.target.value == null
                            ? 30
                            : Math.max(0, Math.min(600, Number(e.target.value))),
                      })
                    }
                    min={0}
                    max={600}
                    step={1}
                    allowDecimals={false}
                    showClearButton={false}
                    disabled={!isAdmin}
                  />
                </div>
              }
            />
            <SettingRow
              id="emailKeepDraftAfterSend"
              title="Keep draft after send"
              description="Leave the compose draft in Drafts after the message is sent, so it can be reused later."
              effect="On = draft stays; Off = draft is deleted after send (default)."
              colors={colors}
              control={
                <Switch
                  id="emailKeepDraftAfterSend"
                  checked={appFormData.emailKeepDraftAfterSend ?? false}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, emailKeepDraftAfterSend: e.target.checked })
                  }
                  disabled={!isAdmin}
                />
              }
            />
            <SettingRow
              id="emailShowAccountDrafts"
              title="Show all drafts on accessible accounts"
              description="Let users see every draft for email accounts they can access, not only their own."
              effect="On = shared account drafts appear with author; opening another user’s draft copies it into your compose. Off = each user sees only their drafts."
              colors={colors}
              control={
                <Switch
                  id="emailShowAccountDrafts"
                  checked={appFormData.emailShowAccountDrafts ?? false}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, emailShowAccountDrafts: e.target.checked })
                  }
                  disabled={!isAdmin}
                />
              }
            />
          </SettingsGroup>

          {applicationSaveFooter}
        </div>
      )}

      {settingsTab === 'ai' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Enable AI features company-wide. Model keys and prompts are configured under System /
            Configuration settings.
          </p>

          <SettingsGroup
            title="AI features"
            description="Each switch unlocks a different product surface."
            colors={colors}
          >
            <SettingRow
              id="enableAiEmails"
              title="AI in email"
              description="Rewrite, proofread, and chat helpers while composing or reading email."
              effect="On shows AI actions in email compose/read. History can sync to AI Chat when the assistant is also on."
              colors={colors}
              control={
                <Switch
                  id="enableAiEmails"
                  checked={appFormData.enableAiEmails ?? false}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, enableAiEmails: e.target.checked })
                  }
                />
              }
            />
            <SettingRow
              id="enableAiAssistant"
              title="AI assistant (CRM & dashboard)"
              description="AI Chat page, client briefs, smart replies, draft todos/appointments from email, and dashboard next actions."
              effect="On unlocks the AI Chat module and CRM/dashboard assistant actions."
              colors={colors}
              control={
                <Switch
                  id="enableAiAssistant"
                  checked={appFormData.enableAiAssistant ?? false}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, enableAiAssistant: e.target.checked })
                  }
                />
              }
            />
          </SettingsGroup>

          {applicationSaveFooter}
        </div>
      )}

      {settingsTab === 'general' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Application identity and default API request behavior.
          </p>

          <SettingsGroup
            title="Branding"
            description="Shown on login and in the product chrome."
            colors={colors}
          >
            <div className="px-4 py-4 sm:px-5">
              <Label htmlFor="appName">Application name</Label>
              <TextInput
                id="appName"
                name="appName"
                value={appFormData.appName || ''}
                onChange={(e) => setAppFormData({ ...appFormData, appName: e.target.value })}
                placeholder="u-comm"
                className="mt-1 max-w-md"
              />
              <p className={cn('mt-1.5 text-xs', colors.textMuted)}>
                Display name for the product (browser title, login, notifications).
              </p>
            </div>
          </SettingsGroup>

          <SettingsGroup
            title="API"
            description="Network timeout for backend requests from the web app."
            colors={colors}
          >
            <SettingRow
              id="apiTimeout"
              title="API timeout (ms)"
              description="Maximum time to wait for an API response before failing."
              effect="Range 1000–300000. Default 30000 (30 seconds)."
              colors={colors}
              control={
                <div className="w-36">
                  <NumberInput
                    id="apiTimeout"
                    name="apiTimeout"
                    value={appFormData.apiTimeout || 30000}
                    onChange={(e) =>
                      setAppFormData({
                        ...appFormData,
                        apiTimeout: (e.target.value as number) || 30000,
                      })
                    }
                    min={1000}
                    max={300000}
                    required
                  />
                </div>
              }
            />
          </SettingsGroup>

          {applicationSaveFooter}
        </div>
      )}

      {settingsTab === 'company' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Letterhead data and logo used on offers and documents. Saved separately from other
            application settings.
          </p>

          <SettingsGroup title="Company details" description="Printed on PDFs and documents." colors={colors}>
            <div className="space-y-4 px-4 py-4 sm:px-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor="companyName">Company name</Label>
                  <TextInput
                    id="companyName"
                    value={companyFormData.companyName}
                    onChange={(e) =>
                      setCompanyFormData({ ...companyFormData, companyName: e.target.value })
                    }
                    placeholder="Company name"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="companyAddress">Address</Label>
                  <Textarea
                    id="companyAddress"
                    value={companyFormData.companyAddress}
                    onChange={(e) =>
                      setCompanyFormData({ ...companyFormData, companyAddress: e.target.value })
                    }
                    placeholder="Address"
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="companyPhone">Phone</Label>
                  <TextInput
                    id="companyPhone"
                    value={companyFormData.companyPhone}
                    onChange={(e) =>
                      setCompanyFormData({ ...companyFormData, companyPhone: e.target.value })
                    }
                    placeholder="Phone"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="companyEmail">Email</Label>
                  <TextInput
                    id="companyEmail"
                    type="email"
                    value={companyFormData.companyEmail}
                    onChange={(e) =>
                      setCompanyFormData({ ...companyFormData, companyEmail: e.target.value })
                    }
                    placeholder="Email"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="signatoryName">Signatory name</Label>
                  <TextInput
                    id="signatoryName"
                    value={companyFormData.signatoryName}
                    onChange={(e) =>
                      setCompanyFormData({ ...companyFormData, signatoryName: e.target.value })
                    }
                    placeholder="Signatory name"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </SettingsGroup>

          <SettingsGroup
            title="Logo"
            description="PNG, JPG, WEBP or GIF, max 2 MB. Used on offer PDFs."
            colors={colors}
          >
            <div className="px-4 py-4 sm:px-5">
              {companyProfile?.logoPath && (
                <div className="mb-3 flex items-center gap-3">
                  {companyLogoLoading ? (
                    <span className={cn('text-sm', colors.textMuted)}>Loading logo…</span>
                  ) : companyLogoBlobUrl ? (
                    <>
                      <img
                        src={companyLogoBlobUrl}
                        alt="Company logo"
                        className={cn('h-16 rounded border object-contain', colors.border)}
                      />
                      <span className={cn('text-sm', colors.textMuted)}>Current logo</span>
                    </>
                  ) : (
                    <span className={cn('text-sm', colors.textMuted)}>Could not load logo</span>
                  )}
                </div>
              )}
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
                className={cn(
                  'block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium',
                  colors.textMuted,
                  colors.primary.replace(/^bg-/, 'file:bg-'),
                  colors.primaryText.replace(/^text-/, 'file:text-'),
                  colors.primaryHover.replace(/^bg-/, 'hover:file:bg-'),
                )}
              />
              {uploadingLogo && (
                <span className={cn('mt-1 block text-sm', colors.textMuted)}>Uploading…</span>
              )}
            </div>
          </SettingsGroup>

          <div
            className={cn(
              'flex flex-col gap-2 border-t pt-6 sm:flex-row sm:items-center sm:justify-between',
              colors.border,
            )}
          >
            <p className={cn('text-xs', colors.textMuted)}>
              Company details use their own Save button (separate from application settings).
            </p>
            <Button
              type="button"
              disabled={savingCompany}
              loading={savingCompany}
              onClick={handleCompanyProfileSubmit}
              className="gap-2 self-end"
            >
              <Save className="h-4 w-4" aria-hidden />
              Save company details
            </Button>
          </div>
        </div>
      )}

      {settingsTab === 'tables' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Defaults for data tables and lists. Users can still override density and page size in their
            own preferences.
          </p>

          <SettingsGroup
            title="Presets"
            description="Saved column layouts and table configurations."
            colors={colors}
          >
            <SettingRow
              id="enableTablePresets"
              title="Table presets"
              description="Allow users to save and apply named table layouts (columns, order, etc.)."
              effect="Off hides preset save/load controls on tables."
              colors={colors}
              control={
                <Switch
                  id="enableTablePresets"
                  checked={appFormData.enableTablePresets ?? true}
                  onChange={(e) =>
                    setAppFormData({ ...appFormData, enableTablePresets: e.target.checked })
                  }
                />
              }
            />
          </SettingsGroup>

          <SettingsGroup
            title="Default list behavior"
            description="Applied when a user has not set personal table preferences."
            colors={colors}
          >
            <div className="grid grid-cols-1 gap-5 px-4 py-4 sm:px-5 md:grid-cols-2">
              <div>
                <Label htmlFor="defaultTablePageSize" required>
                  Default page size
                </Label>
                <NumberInput
                  id="defaultTablePageSize"
                  name="defaultTablePageSize"
                  value={appFormData.defaultTablePageSize || 200}
                  onChange={(e) =>
                    setAppFormData({
                      ...appFormData,
                      defaultTablePageSize: (e.target.value as number) || 200,
                    })
                  }
                  min={10}
                  max={1000}
                  required
                  className="mt-1"
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>Rows per page for new users.</p>
              </div>

              <div>
                <Label htmlFor="defaultTableDensity" required>
                  Default density
                </Label>
                <Select
                  value={appFormData.defaultTableDensity || 'normal'}
                  onChange={(e) =>
                    setAppFormData({
                      ...appFormData,
                      defaultTableDensity: e.target.value as ApplicationSettings['defaultTableDensity'],
                    })
                  }
                  className="mt-1"
                  options={[
                    { value: 'compact', label: 'Compact' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'comfortable', label: 'Comfortable' },
                  ]}
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>Row height / spacing style.</p>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="pageSizeOptions" required>
                  Page size choices
                </Label>
                <TextInput
                  id="pageSizeOptions"
                  name="pageSizeOptions"
                  value={appFormData.pageSizeOptions?.join(', ') || '50, 100, 200, 500'}
                  onChange={(e) => {
                    const value = e.target.value;
                    const options = value
                      .split(',')
                      .map((v) => parseInt(v.trim(), 10))
                      .filter((v) => !isNaN(v) && v > 0);
                    setAppFormData({
                      ...appFormData,
                      pageSizeOptions: options.length > 0 ? options : [50, 100, 200, 500],
                    });
                  }}
                  placeholder="50, 100, 200, 500"
                  className="mt-1"
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>
                  Comma-separated values shown in the page-size dropdown (e.g. 50, 100, 200, 500).
                </p>
              </div>
            </div>
          </SettingsGroup>

          {applicationSaveFooter}
        </div>
      )}

      {settingsTab === 'dashboard' && (
        <div className="space-y-5">
          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            Dashboard layout uses a column grid. Widget widths are in column units (must fit within
            grid columns).
          </p>

          <SettingsGroup
            title="Grid & widget widths"
            description="Example with 6 columns: 6 stats, 3 lists, or 2 charts per row."
            colors={colors}
          >
            <div className="grid grid-cols-1 gap-5 px-4 py-4 sm:grid-cols-2 sm:px-5 lg:grid-cols-4">
              <div>
                <Label htmlFor="dashboardGridColumns">Grid columns</Label>
                <NumberInput
                  id="dashboardGridColumns"
                  name="dashboardGridColumns"
                  value={appFormData.dashboardGridColumns ?? defaultDashboardGrid.gridColumns}
                  onChange={(e) =>
                    setAppFormData({
                      ...appFormData,
                      dashboardGridColumns: e.target.value ?? defaultDashboardGrid.gridColumns,
                    })
                  }
                  min={4}
                  max={12}
                  className="mt-1"
                />
                <p className={cn('mt-1 text-xs', colors.textMuted)}>Total columns per row (4–12).</p>
              </div>
              <div>
                <Label htmlFor="dashboardStatCardWidth">Stat card width</Label>
                <NumberInput
                  id="dashboardStatCardWidth"
                  name="dashboardStatCardWidth"
                  value={appFormData.dashboardStatCardWidth ?? defaultDashboardGrid.statCardWidth}
                  onChange={(e) =>
                    setAppFormData({
                      ...appFormData,
                      dashboardStatCardWidth: e.target.value ?? defaultDashboardGrid.statCardWidth,
                    })
                  }
                  min={1}
                  max={12}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dashboardListWidgetWidth">List widget width</Label>
                <NumberInput
                  id="dashboardListWidgetWidth"
                  name="dashboardListWidgetWidth"
                  value={appFormData.dashboardListWidgetWidth ?? defaultDashboardGrid.listWidgetWidth}
                  onChange={(e) =>
                    setAppFormData({
                      ...appFormData,
                      dashboardListWidgetWidth:
                        e.target.value ?? defaultDashboardGrid.listWidgetWidth,
                    })
                  }
                  min={1}
                  max={12}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="dashboardChartWidth">Chart width</Label>
                <NumberInput
                  id="dashboardChartWidth"
                  name="dashboardChartWidth"
                  value={appFormData.dashboardChartWidth ?? defaultDashboardGrid.chartWidth}
                  onChange={(e) =>
                    setAppFormData({
                      ...appFormData,
                      dashboardChartWidth: e.target.value ?? defaultDashboardGrid.chartWidth,
                    })
                  }
                  min={1}
                  max={12}
                  className="mt-1"
                />
              </div>
            </div>
          </SettingsGroup>

          {applicationSaveFooter}
        </div>
      )}
    </>
  );

  const settingsLayout = (
    <div className="flex min-h-0 flex-col lg:min-h-[32rem] lg:flex-row">
      <ApplicationSettingsNav activeTab={settingsTab} onChange={handleSettingsTabChange} />
      <div className="min-w-0 flex-1">
        {currentTab ? (
          <div className={cn('hidden border-b px-6 py-4 lg:block', colors.border, colors.surfaceHover)}>
            <h2 className={cn('text-base font-semibold', colors.text)}>{currentTab.label}</h2>
            <p className={cn('mt-0.5 text-sm', colors.textMuted)}>{currentTab.description}</p>
          </div>
        ) : null}
        <div className="p-4 sm:p-6">{settingsPanels}</div>
      </div>
    </div>
  );

  if (embedded) {
    return <div className="-m-4 overflow-hidden sm:-m-6">{settingsLayout}</div>;
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
        <div className={cn('rounded-2xl border px-5 py-5 sm:px-6', colors.border, colors.surface)}>
          <div className="flex items-start gap-4">
            <span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', colors.info)}>
              <Settings2 className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className={cn('text-lg font-semibold tracking-tight', colors.text)}>
                Application settings
              </h1>
              <p className={cn('mt-1 text-sm leading-relaxed', colors.textMuted)}>
                Company-wide options, organized by topic. Pick a section on the left, change what you
                need, then save. Each control includes a short “Effect” note so you know what changes.
              </p>
            </div>
          </div>
        </div>

        <Card className="overflow-hidden">{settingsLayout}</Card>
      </div>
    </div>
  );
};

export default ApplicationSettingsForm;
